import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { withRouter } from 'react-router';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import * as adminActions from '../../actions/admin';
import { Responsibility } from '../../types/responsibility.type';
import { RootState } from '../../reducers';
import { t } from '../../i18n';
import DeleteModalComponent from '../../components/Modal/DeleteModal';
import ModalComponent from '../../components/Modal/ModalComponent';
import './Responsibilities.scss';

interface Props {
    history: any;
    loading: boolean;
    actions: any;
    production: any;
    intl: any;
    admin: any;
    adminActions: any;
    productionActions: any;
    buttonLoader: boolean;
}

type CategoryValue = 'activity' | 'production';
type FilterValue = CategoryValue | 'uncategorized';

const CATEGORY_META: Record<CategoryValue, { labelId: string; defaultLabel: string; color: string }> = {
    activity:   { labelId: 'admin.responsibilities.category.activity',   defaultLabel: 'Aktivitet (Projektimi)', color: '#3b82f6' },
    production: { labelId: 'admin.responsibilities.category.production', defaultLabel: 'Procese të prodhimit', color: '#eab308' },
};

const CATEGORY_KEYS: CategoryValue[] = ['activity', 'production'];

type EditDraft = {
    id: number | 'new';
    name: string;
    category: CategoryValue | null;
};

const Responsibilities = (props: Props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<FilterValue[]>([]);
    const [draft, setDraft] = useState<EditDraft | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [editCategoryDropOpen, setEditCategoryDropOpen] = useState(false);

    // Drag-reorder state. localOrder shadows props.production.responsibilities
    // after a drop so the UI doesn't flash to the old order while the PUT is in
    // flight. Cleared once the next fetchResponsibilities resolves.
    const [localOrder, setLocalOrder] = useState<number[] | null>(null);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [dragId, setDragId] = useState<number | null>(null);
    const [dropTargetId, setDropTargetId] = useState<number | null>(null);

    const nameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        props.productionActions.fetchResponsibilities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (draft) nameInputRef.current?.focus();
        setEditCategoryDropOpen(false);
    }, [draft?.id]);

    useEffect(() => {
        if (!editCategoryDropOpen) return;
        const handler = () => setEditCategoryDropOpen(false);
        const id = window.setTimeout(() => document.addEventListener('click', handler), 0);
        return () => {
            window.clearTimeout(id);
            document.removeEventListener('click', handler);
        };
    }, [editCategoryDropOpen]);

    useEffect(() => {
        if (!bulkOpen) return;
        const handler = () => setBulkOpen(false);
        const id = window.setTimeout(() => document.addEventListener('click', handler), 0);
        return () => {
            window.clearTimeout(id);
            document.removeEventListener('click', handler);
        };
    }, [bulkOpen]);

    const allResponsibilities: Responsibility[] = props.production.responsibilities || [];

    // After a drop we keep the new order locally until the next refetch lands.
    // Once redux returns the server-ordered list, drop our optimistic copy.
    useEffect(() => {
        if (localOrder == null) return;
        const sameLength = allResponsibilities.length === localOrder.length;
        const serverIds = allResponsibilities.map(r => r.id);
        const matchesServer = sameLength && localOrder.every((id, i) => id === serverIds[i]);
        if (matchesServer) setLocalOrder(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allResponsibilities]);

    const orderedResponsibilities: Responsibility[] = useMemo(() => {
        if (!localOrder) return allResponsibilities;
        const byId = new Map<number, Responsibility>();
        allResponsibilities.forEach(r => { if (r.id != null) byId.set(r.id, r); });
        const out: Responsibility[] = [];
        localOrder.forEach(id => { const r = byId.get(id); if (r) out.push(r); });
        // Append any new rows that arrived from the server but aren't in localOrder yet
        allResponsibilities.forEach(r => { if (r.id != null && !localOrder.includes(r.id)) out.push(r); });
        return out;
    }, [allResponsibilities, localOrder]);

    const stats = useMemo(() => {
        const counts = { activity: 0, production: 0, uncategorized: 0 };
        allResponsibilities.forEach(r => {
            const cat = r.category;
            if (cat && cat in counts) (counts as any)[cat]++;
            else counts.uncategorized++;
        });
        return { total: allResponsibilities.length, ...counts };
    }, [allResponsibilities]);

    const filterActive = search.trim().length > 0 || categoryFilter.length > 0;

    const filteredResponsibilities = useMemo(() => {
        let list = orderedResponsibilities;
        const q = search.trim().toLowerCase();
        if (q) list = list.filter(r => (r.label || '').toLowerCase().includes(q));
        if (categoryFilter.length > 0) {
            list = list.filter(r => {
                const cat = (r.category ?? 'uncategorized') as FilterValue;
                return categoryFilter.includes(cat);
            });
        }
        return list;
    }, [orderedResponsibilities, search, categoryFilter]);

    // ── Drag-reorder handlers ──────────────────────────────────────────────
    const handleDragStart = (e: React.DragEvent, id: number) => {
        if (filterActive) { e.preventDefault(); return; }
        setDragId(id);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(id)); } catch (_) { /* noop */ }
    };

    const handleDragOver = (e: React.DragEvent, id: number) => {
        if (filterActive || dragId == null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTargetId !== id) setDropTargetId(id);
    };

    const handleDragEnd = () => {
        setDragId(null);
        setDropTargetId(null);
    };

    const handleDrop = (e: React.DragEvent, targetId: number) => {
        e.preventDefault();
        const sourceId = dragId;
        setDragId(null);
        setDropTargetId(null);
        if (sourceId == null || sourceId === targetId) return;

        const ids = orderedResponsibilities.map(r => r.id!).filter((id): id is number => id != null);
        const sourceIdx = ids.indexOf(sourceId);
        const targetIdx = ids.indexOf(targetId);
        if (sourceIdx === -1 || targetIdx === -1) return;

        const next = [...ids];
        next.splice(sourceIdx, 1);
        next.splice(targetIdx, 0, sourceId);
        setLocalOrder(next);
        setSaveStatus('saving');

        const payload = next.map((id, idx) => ({ id, sort_order: idx + 1 }));
        Promise.resolve(props.adminActions.reorderResponsibilities(payload))
            .then(() => {
                setSaveStatus('saved');
                // Refetch quietly so the redux state reflects the new sort_order;
                // the useEffect above will then clear localOrder.
                props.productionActions.fetchResponsibilities();
            })
            .catch(() => {
                setSaveStatus('error');
                setLocalOrder(null); // revert to server order
                toast.error(fm('admin.responsibilities.reorderFailed', 'Renditja nuk u ruajt'));
            });
    };

    const startCreate = () => {
        setDraft({ id: 'new', name: '', category: null });
        setSelectedIds(new Set());
    };

    const startEdit = (r: Responsibility) => {
        setDraft({ id: r.id!, name: r.label || '', category: (r.category ?? null) as CategoryValue | null });
    };

    const cancelEdit = () => setDraft(null);

    const saveDraft = () => {
        if (!draft) return;
        if (!draft.name.trim()) {
            toast.error(t('admin.toastFillField'));
            return;
        }
        const payload = { name: draft.name.trim(), category: draft.category };
        if (draft.id === 'new') {
            props.adminActions.createResponsibility(payload).then(() => {
                props.productionActions.fetchResponsibilities();
                setDraft({ id: 'new', name: '', category: null });
                window.setTimeout(() => nameInputRef.current?.focus(), 50);
            });
        } else {
            props.adminActions.editResponsibility(draft.id, payload).then(() => {
                props.productionActions.fetchResponsibilities();
                setDraft(null);
            });
        }
    };

    const askDelete = (id: number) => {
        setDeleteTargetId(id);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (deleteTargetId == null) return;
        props.adminActions.deleteResponsibility(deleteTargetId).then(() => {
            props.productionActions.fetchResponsibilities();
            setIsDeleteModalOpen(false);
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(deleteTargetId);
                return next;
            });
            setDeleteTargetId(null);
        });
    };

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const visibleIds = filteredResponsibilities.map(r => r.id!).filter(Boolean);
        if (visibleIds.every(id => selectedIds.has(id))) setSelectedIds(new Set());
        else setSelectedIds(new Set(visibleIds));
    };

    const toggleCategoryFilter = (value: FilterValue) => {
        setCategoryFilter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const bulkAssignCategory = async (category: CategoryValue | null) => {
        if (selectedIds.size === 0) return;
        setBulkOpen(false);
        setBulkSubmitting(true);
        const idMap = new Map(allResponsibilities.map(r => [r.id!, r] as const));
        const ids = Array.from(selectedIds);
        let ok = 0;
        let fail = 0;
        for (const id of ids) {
            const r = idMap.get(id);
            if (!r) { fail++; continue; }
            try {
                await props.adminActions.editResponsibility(id, { name: r.label || '', category });
                ok++;
            } catch {
                fail++;
            }
        }
        await props.productionActions.fetchResponsibilities();
        setSelectedIds(new Set());
        setBulkSubmitting(false);
        if (fail === 0) {
            toast.success(fm('admin.responsibilities.bulkSuccess', '{count} procese u përditësuan', { count: ok }));
        } else {
            toast.error(fm('admin.responsibilities.bulkPartial', '{success} u përditësuan, {failure} dështuan', { success: ok, failure: fail }));
        }
    };

    const renderCategoryPill = (cat: CategoryValue | null | undefined) => {
        if (!cat || !CATEGORY_META[cat]) {
            return (
                <span className="rsp-pill rsp-pill-none">
                    <span className="rsp-pill-dot" />
                    <FormattedMessage id="admin.responsibilities.category.none" defaultMessage="Pa kategori" />
                </span>
            );
        }
        const meta = CATEGORY_META[cat];
        return (
            <span className="rsp-pill" style={{ color: meta.color, borderColor: meta.color }}>
                <span className="rsp-pill-dot" style={{ background: meta.color }} />
                <FormattedMessage id={meta.labelId} defaultMessage={meta.defaultLabel} />
            </span>
        );
    };

    const renderEditRow = () => {
        if (!draft) return null;
        const selectedMeta = draft.category && CATEGORY_META[draft.category] ? CATEGORY_META[draft.category] : null;
        return (
            <div className="rsp-row rsp-row-edit">
                <div className="rsp-cell rsp-col-drag" aria-hidden />
                <div className="rsp-cell rsp-col-check" aria-hidden />
                <div className="rsp-cell rsp-col-name">
                    <input
                        ref={nameInputRef}
                        type="text"
                        className="rsp-input"
                        value={draft.name}
                        onChange={e => setDraft({ ...draft, name: e.target.value })}
                        onKeyDown={e => {
                            if (e.key === 'Enter') saveDraft();
                            else if (e.key === 'Escape') cancelEdit();
                        }}
                        placeholder={fm('admin.responsibilities.fieldName', 'Emri i procesit')}
                    />
                </div>
                <div className="rsp-cell rsp-col-category">
                    <div className="rsp-edit-category-dd" onClick={e => e.stopPropagation()}>
                        <button
                            type="button"
                            className={`rsp-edit-category-btn ${editCategoryDropOpen ? 'open' : ''} ${selectedMeta ? '' : 'rsp-edit-category-btn-none'}`}
                            onClick={() => setEditCategoryDropOpen(!editCategoryDropOpen)}
                            style={selectedMeta ? { color: selectedMeta.color, borderColor: selectedMeta.color } : undefined}
                        >
                            <span className="rsp-pill-dot" style={{ background: selectedMeta ? selectedMeta.color : '#cbd5e1' }} />
                            <span className="rsp-edit-category-label">
                                {selectedMeta ? (
                                    <FormattedMessage id={selectedMeta.labelId} defaultMessage={selectedMeta.defaultLabel} />
                                ) : (
                                    <FormattedMessage id="admin.responsibilities.category.none" defaultMessage="Pa kategori" />
                                )}
                            </span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>
                        {editCategoryDropOpen && (
                            <div className="rsp-edit-category-menu">
                                {/* "Pa kategori" is intentionally not selectable from the menu — it's only a fallback display when category is null. */}
                                {CATEGORY_KEYS.map(k => (
                                    <button
                                        key={k}
                                        type="button"
                                        className="rsp-edit-category-item"
                                        onClick={() => { setDraft({ ...draft, category: k }); setEditCategoryDropOpen(false); }}
                                    >
                                        <span className="rsp-pill-dot" style={{ background: CATEGORY_META[k].color }} />
                                        <FormattedMessage id={CATEGORY_META[k].labelId} defaultMessage={CATEGORY_META[k].defaultLabel} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="rsp-cell rsp-col-usage" aria-hidden />
                <div className="rsp-cell rsp-col-actions">
                    <button type="button" className="rsp-icon-btn rsp-icon-save" onClick={saveDraft} disabled={props.buttonLoader} title={fm('admin.responsibilities.save', 'Ruaj')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </button>
                    <button type="button" className="rsp-icon-btn" onClick={cancelEdit} title={fm('admin.responsibilities.cancel', 'Anulo')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        );
    };


    const visibleIds = filteredResponsibilities.map(r => r.id!).filter(Boolean);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    const someVisibleSelected = !allVisibleSelected && visibleIds.some(id => selectedIds.has(id));

    return (
        <div className="rsp-page">
            <div className="rsp-header">
                <h1 className="rsp-title">
                    <FormattedMessage id="admin.responsibilities.pageTitle" defaultMessage="Proceset e prodhimit" />
                </h1>
                <div className="rsp-header-right">
                    {saveStatus === 'saving' && (
                        <span className="rsp-save-status rsp-save-status--saving">
                            <span className="rsp-save-dot" />
                            <FormattedMessage id="admin.responsibilities.saving" defaultMessage="Duke u ruajtur..." />
                        </span>
                    )}
                    {saveStatus === 'saved' && (
                        <span className="rsp-save-status rsp-save-status--saved">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <FormattedMessage id="admin.responsibilities.saved" defaultMessage="Ruajtur" />
                        </span>
                    )}
                    <button type="button" className="rsp-btn-primary" onClick={startCreate} disabled={draft?.id === 'new'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        <FormattedMessage id="admin.responsibilities.create" defaultMessage="Krijo proces" />
                    </button>
                </div>
            </div>

            <div className="rsp-stats">
                <button
                    type="button"
                    className={`rsp-stat-card rsp-stat-total ${categoryFilter.length === 0 ? 'active' : ''}`}
                    onClick={() => setCategoryFilter([])}
                >
                    <span className="rsp-stat-num">{stats.total}</span>
                    <span className="rsp-stat-label"><FormattedMessage id="admin.responsibilities.statTotal" defaultMessage="Gjithsej" /></span>
                </button>
                {CATEGORY_KEYS.map(k => (
                    <button
                        key={k}
                        type="button"
                        className={`rsp-stat-card ${categoryFilter.includes(k) ? 'active' : ''}`}
                        onClick={() => toggleCategoryFilter(k)}
                        style={{ ['--accent' as any]: CATEGORY_META[k].color }}
                    >
                        <span className="rsp-stat-num">{stats[k]}</span>
                        <span className="rsp-stat-label"><FormattedMessage id={CATEGORY_META[k].labelId} defaultMessage={CATEGORY_META[k].defaultLabel} /></span>
                    </button>
                ))}
                <button
                    type="button"
                    className={`rsp-stat-card rsp-stat-none ${categoryFilter.includes('uncategorized') ? 'active' : ''}`}
                    onClick={() => toggleCategoryFilter('uncategorized')}
                >
                    <span className="rsp-stat-num">{stats.uncategorized}</span>
                    <span className="rsp-stat-label"><FormattedMessage id="admin.responsibilities.category.none" defaultMessage="Pa kategori" /></span>
                </button>
            </div>

            <div className="rsp-toolbar">
                <div className="rsp-search">
                    <svg className="rsp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input
                        type="text"
                        className="rsp-search-input"
                        placeholder={fm('admin.responsibilities.searchPlaceholder', 'Kërko proces...')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button type="button" className="rsp-search-clear" onClick={() => setSearch('')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                {(search || categoryFilter.length > 0) && (
                    <button type="button" className="rsp-clear-all" onClick={() => { setSearch(''); setCategoryFilter([]); }}>
                        <FormattedMessage id="admin.responsibilities.clearFilters" defaultMessage="Pastro filtrat" />
                    </button>
                )}
            </div>

            {filterActive && (
                <div className="rsp-reorder-hint">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <FormattedMessage
                        id="admin.responsibilities.reorderDisabledHint"
                        defaultMessage="Pastro filtrat për të ndryshuar rendin e proceseve"
                    />
                </div>
            )}

            <div className="rsp-table">
                <div className="rsp-row rsp-row-header">
                    <div className="rsp-cell rsp-col-drag" aria-hidden />
                    <div className="rsp-cell rsp-col-check">
                        <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            ref={el => { if (el) el.indeterminate = someVisibleSelected; }}
                            onChange={toggleSelectAll}
                            aria-label={fm('admin.responsibilities.selectAll', 'Zgjidh të gjitha')}
                        />
                    </div>
                    <div className="rsp-cell rsp-col-name"><FormattedMessage id="admin.responsibilities.colName" defaultMessage="Procesi i prodhimit" /></div>
                    <div className="rsp-cell rsp-col-category"><FormattedMessage id="admin.responsibilities.colCategory" defaultMessage="Kategoria" /></div>
                    <div className="rsp-cell rsp-col-usage"><FormattedMessage id="admin.responsibilities.colUsage" defaultMessage="Artikuj" /></div>
                    <div className="rsp-cell rsp-col-actions"><FormattedMessage id="admin.responsibilities.colActions" defaultMessage="Veprime" /></div>
                </div>

                {draft?.id === 'new' && renderEditRow()}

                <div className="rsp-row-body">
                    {props.loading ? (
                        <div className="rsp-empty"><FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." /></div>
                    ) : filteredResponsibilities.length === 0 ? (
                        <div className="rsp-empty">
                            {search || categoryFilter.length > 0 ? (
                                <FormattedMessage id="admin.responsibilities.noResults" defaultMessage="Asnjë proces nuk përputhet me filtrat" />
                            ) : (
                                <FormattedMessage id="admin.responsibilities.empty" defaultMessage="Nuk ka procese. Klikoni 'Krijo proces' për të filluar." />
                            )}
                        </div>
                    ) : (
                        filteredResponsibilities.map(r => {
                            if (draft && draft.id === r.id) {
                                return <React.Fragment key={r.id}>{renderEditRow()}</React.Fragment>;
                            }
                            const isSelected = selectedIds.has(r.id!);
                            const isDragging = dragId === r.id;
                            const isDropTarget = dropTargetId === r.id && dragId !== r.id;
                            const usage = (r as any).articlesCount ?? 0;
                            return (
                                <div
                                    key={r.id}
                                    className={`rsp-row ${isSelected ? 'selected' : ''} rsp-row-cat-${r.category ?? 'none'} ${isDragging ? 'is-dragging' : ''} ${isDropTarget ? 'is-drop-target' : ''}`}
                                    draggable={!filterActive}
                                    onDragStart={(e) => handleDragStart(e, r.id!)}
                                    onDragOver={(e) => handleDragOver(e, r.id!)}
                                    onDrop={(e) => handleDrop(e, r.id!)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <div
                                        className={`rsp-cell rsp-col-drag ${filterActive ? 'is-disabled' : ''}`}
                                        title={filterActive
                                            ? fm('admin.responsibilities.reorderDisabledTitle', 'Pastro filtrat për të ndryshuar rendin')
                                            : fm('admin.responsibilities.dragHandleTitle', 'Tërhiq për të ndryshuar rendin')}
                                        aria-label={fm('admin.responsibilities.dragHandleTitle', 'Tërhiq për të ndryshuar rendin')}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                            <circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" />
                                            <circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" />
                                        </svg>
                                    </div>
                                    <div className="rsp-cell rsp-col-check">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelect(r.id!)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="rsp-cell rsp-col-name" onClick={() => startEdit(r)}>
                                        <span className="rsp-name-text">{r.label}</span>
                                    </div>
                                    <div className="rsp-cell rsp-col-category" onClick={() => startEdit(r)}>
                                        {renderCategoryPill(r.category as CategoryValue | null | undefined)}
                                    </div>
                                    <div className="rsp-cell rsp-col-usage">
                                        <span className={`rsp-usage-badge ${usage > 0 ? '' : 'is-zero'}`}>
                                            <FormattedMessage
                                                id="admin.responsibilities.usageCount"
                                                defaultMessage="{count, plural, one {# artikull} other {# artikuj}}"
                                                values={{ count: usage }}
                                            />
                                        </span>
                                    </div>
                                    <div className="rsp-cell rsp-col-actions">
                                        <button type="button" className="rsp-icon-btn" onClick={() => startEdit(r)} title={fm('common.action.edit', 'Ndrysho')}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button type="button" className="rsp-icon-btn rsp-icon-danger" onClick={() => askDelete(r.id!)} title={fm('common.action.delete', 'Fshi')}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {selectedIds.size > 0 && (
                <div className="rsp-bulk-bar">
                    <span className="rsp-bulk-count">
                        <FormattedMessage
                            id="admin.responsibilities.bulkSelected"
                            defaultMessage="{count, plural, one {# i zgjedhur} other {# të zgjedhur}}"
                            values={{ count: selectedIds.size }}
                        />
                    </span>
                    <div className="rsp-bulk-actions">
                        <div className="rsp-bulk-dropdown" onClick={e => e.stopPropagation()}>
                            <button type="button" className="rsp-bulk-btn" onClick={() => setBulkOpen(!bulkOpen)} disabled={bulkSubmitting}>
                                <FormattedMessage id="admin.responsibilities.bulkAssignCategory" defaultMessage="Cakto kategorinë" />
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                            </button>
                            {bulkOpen && (
                                <div className="rsp-bulk-menu">
                                    {/* Bulk action also doesn't offer "Pa kategori" — admins set categories, never unset them. */}
                                    {CATEGORY_KEYS.map(k => (
                                        <button key={k} type="button" className="rsp-bulk-menu-item" onClick={() => bulkAssignCategory(k)}>
                                            <span className="rsp-pill-dot" style={{ background: CATEGORY_META[k].color }} />
                                            <FormattedMessage id={CATEGORY_META[k].labelId} defaultMessage={CATEGORY_META[k].defaultLabel} />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button type="button" className="rsp-bulk-clear" onClick={() => setSelectedIds(new Set())} disabled={bulkSubmitting}>
                            <FormattedMessage id="admin.responsibilities.bulkClear" defaultMessage="Çregjistro" />
                        </button>
                    </div>
                </div>
            )}

            <ModalComponent
                isModalHeader
                isOpen={isDeleteModalOpen}
                close={setIsDeleteModalOpen}
            >
                <DeleteModalComponent
                    history={props.history}
                    close={() => setIsDeleteModalOpen(false)}
                    deleteFunction={confirmDelete}
                />
            </ModalComponent>
        </div>
    );
};

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        production: state.production,
        admin: state.admin,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        productionActions: bindActionCreators(productionActions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(injectIntl(Responsibilities as any)));
